import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlagService, FLAGS } from 'app/services/feature-flag.service';
import { KeyboardShortcutsService } from 'app/services/keyboard-shortcuts.service';
import { RouteMemoService } from 'app/services/route-memo.service';
import { UserService } from 'app/services/user.service';
import { Subject } from 'rxjs';
import { filter, map, pluck, switchMap, takeUntil } from 'rxjs/operators';
import { Button } from '../models';
import { ScreenService } from '../services/screen.service';

@Component({
	// TODO: OnPush breaks app navigation completely
	// changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-nav-tabs',
	template: `<div class="nav-tabs">
		<ng-container *ngFor="let button of buttons">
			<div *ngIf="isVisible(button, roles$ | async)" class="nav-tab">
				<div class="nav-button-wrapper">
					<app-nav-button
						[selected]="routeMemo.selectedRoute$ | async | matchesRoute : button.route"
						[content]="button.title"
						[icon]="button.imgUrl"
						(onClick)="router.navigateByUrl(button.route)"></app-nav-button>
				</div></div
		></ng-container>
	</div> `,
	styleUrls: ['./nav-tabs.component.scss'],
})
export class NavTabsComponent implements OnInit, OnDestroy {
	@Input() disableKeyboardShortcuts = false;

	private destroyed$ = new Subject<void>();

	buttons: Button[] = [
		{
			title: 'Home',
			route: '/main/passes',
			imgUrl: 'school',
			requiredRoles: ['_profile_teacher', 'access_passes', '_profile_student'],
			hidden: false,
		},
		{
			title: 'Hall Monitor',
			route: '/main/hallmonitor',
			imgUrl: 'hall-monitor',
			requiredRoles: ['access_hall_monitor'],
			hidden: false,
		},
		{
			title: 'Calendar',
			route: '/main/passes-calendar',
			imgUrl: 'calendar',
			requiredRoles: ['_profile_teacher', 'access_teacher_room', '_profile_student'],
			hidden: false,
		},
		{
			title: 'Flex',
			route: this.featureService.isFeatureEnabledV2(FLAGS.ActivityManagementOverview) ? '/main/activities/overview' : '/main/activities',
			imgUrl: 'flex',
			requiredRoles: ['_profile_admin', '_profile_teacher'],
			hidden: !this.featureService.isFeatureEnabledV2(FLAGS.ActivityManagement),
		},
		{
			title: 'Admin',
			route: '/admin',
			imgUrl: 'admin',
			requiredRoles: ['_profile_admin'],
			hidden: false,
		},
	];

	roles$ = this.userService.userJSON$.pipe(
		filter((u) => !!u),
		map((user) => user.roles),
		takeUntil(this.destroyed$)
	);

	keyboardShortcuts$ = this.roles$.pipe(
		switchMap((userRoles) =>
			this.shortcutsService.onPressKeyEvent$.pipe(
				pluck('key'),
				// Only handle single digit key presses, to navigate to that tab
				map((key) => parseInt(key[0])),
				filter((key) => !isNaN(key)),
				map<number, [number, string[]]>((key) => [key, userRoles])
			)
		),
		takeUntil(this.destroyed$)
	);

	constructor(
		protected router: Router,
		protected routeMemo: RouteMemoService,
		private featureService: FeatureFlagService,
		protected userService: UserService,
		private shortcutsService: KeyboardShortcutsService,
		public screenService: ScreenService
	) {}

	ngOnInit(): void {
		this.keyboardShortcuts$.pipe(takeUntil(this.destroyed$)).subscribe(([key, userRoles]) => {
			if (this.disableKeyboardShortcuts) {
				return;
			}
			const visibleButtons = this.buttons.filter((button) => this.isVisible(button, userRoles));
			const targetButton = visibleButtons[key - 1];
			if (targetButton) {
				this.router.navigateByUrl(targetButton.route);
			}
		});
	}

	ngOnDestroy(): void {
		this.destroyed$.next(undefined);
		this.destroyed$.complete();
	}

	protected isVisible(button: Button, userRoles: string[] | null): boolean {
		return !button.hidden && this.hasAnyRole(button.requiredRoles, userRoles) && !this.hasAnyRole(button.hiddenFrom || [], userRoles);
	}

	protected hasAnyRole(desiredRoles: string[], userRoles: string[] | null): boolean {
		return userRoles != null && desiredRoles.some((role) => userRoles.includes(role));
	}
}
